<template>
  <FusionBaseDialog
    @close="close"
    @after-leave="afterLeave"
    :open="open"
    :error="error"
    :secondaryError="secondaryError"
    :shaded="isShaded"
    :title="$t('map.deviceSettings.title')"
    icon="mdi-cog-box"
    :persistentOverlay="true"
    padded
    data-cy="deviceSettingsDialog"
    customCss="device-settings"
  >
    <!-- default view -->
    <template v-if="showDefaultView">
      <v-progress-circular v-show="isLoading" indeterminate color="primary"></v-progress-circular>
      <v-tabs align-tabs="center" fixed-tabs v-model="tab" color="primary" :inert="isLoading">
        <v-tab
          data-cy="generalTab"
          :value="'generalTab'"
          :class="{ 'text-error': !generalTab.valid }"
        >
          <span class="tab-title">{{ $t('map.deviceSettings.general') }}</span>
        </v-tab>
        <v-tab
          data-cy="locationTab"
          :value="'locationTab'"
          :class="{ 'text-error': !locationTab.valid }"
        >
          <span class="tab-title">{{ $t('map.deviceSettings.location') }}</span>
        </v-tab>
        <v-tab
          v-if="calibrationTab.show"
          data-cy="calibrationTab"
          :value="'calibrationTab'"
          :class="{ 'text-error': !calibrationTab.valid }"
        >
          <span class="tab-title">{{ $t('map.deviceSettings.calibration') }}</span>
        </v-tab>
      </v-tabs>

      <v-tabs-window v-model="tab" :inert="isLoading">
        <v-window-item :eager="true" value="generalTab">
          <v-form v-model="generalTab.valid" ref="generalTab">
            <v-card flat>
              <DeviceAttributeTextarea
                v-model="generalTab.deviceName"
                :label="$t('activateDevice.deviceName')"
                :rules="[rules.required]"
                required
                :cyTag="'settingsDeviceName'"
              />
              <div data-cy="settingsIsPublicContainer">
                <v-switch
                  v-model="generalTab.isPublic"
                  :label="$t('activateDevice.isPublic')"
                  color="primary"
                  :hint="$t('activateDevice.isPublicDescription')"
                  persistent-hint
                  :disabled="!generalTab.enablePublicToggle"
                  data-cy="settingsIsPublic"
                ></v-switch>
              </div>
              <div v-if="generalTab.showIndoorSensor" class="secondary-indicator">
                <v-switch
                  v-model="generalTab.indoorSensor"
                  :disabled="generalTab.isIndoorOnly"
                  color="primary"
                  :hint="indoorSensorHint"
                  :persistent-hint="generalTab.isIndoorOnly"
                  data-cy="settingsIsIndoor"
                >
                  <template v-slot:label>
                    {{ $t('activateDevice.indoorSensor') }}
                    <div class="device-type-indicator">
                      <img
                        v-if="generalTab.indoorSensor"
                        :src="indoorPinSrc"
                        alt="Indoor Device Pin"
                      />
                      <img
                        v-if="!generalTab.indoorSensor"
                        :src="outdoorPinSrc"
                        alt="Outdoor Device Pin"
                      />
                    </div>
                  </template>
                </v-switch>
              </div>
              <!-- Logging and Cleaning Intervals -->
              <v-row v-if="generalTab.showLoggingCleaningIntervals">
                <v-col cols="6">
                  <v-select
                    v-model="loggingInterval"
                    :items="loggingIntervalItems"
                    :label="$t('map.deviceSettings.loggingInterval')"
                    data-cy="loggingIntervalDropDown"
                  >
                    <template v-slot:item="{ item, props }">
                      <v-list-item v-bind="props" :data-cy="`${item.title}`"></v-list-item>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="cleaningInterval"
                    :items="cleaningIntervalItems"
                    :label="$t('map.deviceSettings.cleaningInterval')"
                    data-cy="cleaningIntervalDropDown"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip location="bottom" max-width="400">
                        <template v-slot:activator="{ props }">
                          <span v-bind="props" icon @mousedown.stop>
                            <v-icon size="x-small" data-cy="cleaningIntervalTooltip">
                              mdi-information-outline
                            </v-icon>
                          </span>
                        </template>
                        <span>
                          {{ $t('map.deviceSettings.cleaningIntervalToolTip') }}
                        </span>
                      </v-tooltip>
                    </template>
                    <template v-slot:item="{ props }">
                      <v-list-item v-bind="props"> </v-list-item>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <!-- Unregister -->
              <div class="mt-6">
                <v-btn
                  @click="unregisterWarningOpen = true"
                  variant="tonal"
                  color="error"
                  class="my-2"
                  data-cy="unregisterButton"
                  >{{ $t('deviceUnregister.unregisterButton') }}</v-btn
                >
                <div class="hint-text">
                  {{ $t('deviceUnregister.unregisterHelp') }}
                </div>
              </div>
            </v-card>
          </v-form>
        </v-window-item>

        <v-window-item :eager="true" value="locationTab">
          <v-form v-model="locationTab.valid" ref="locationTab">
            <v-card flat>
              <v-card-title class="tab-content__title">
                {{ $t('activateDevice.location') }}
              </v-card-title>
              <DeviceAttributeTextarea
                v-model="locationTab.locationName"
                :label="$t('activateDevice.deviceLocationName')"
                :cyTag="'deviceSettingsLocationName'"
              />
              <div data-cy="settingsSetLocationContainer">
                <v-btn
                  :loading="gettingLocation"
                  @click="useBrowserLocation"
                  color="primary"
                  variant="tonal"
                  :disabled="locationError || !locationTab.allowSetLocation"
                  class="my-2"
                  data-cy="useBrowserLocation"
                  >{{ $t('activateDevice.useMyLocation') }}</v-btn
                >
                <div class="hint-text text-error opacity-100">{{ locationErrorMessage }}</div>
                <div
                  class="hint-text"
                  :class="{ 'hint-text--disabled': !locationTab.allowSetLocation }"
                >
                  {{ $t('activateDevice.useMyLocationHint') }}
                </div>
                <br />
                <v-text-field
                  v-model="locationTab.coords.latitude"
                  :label="$t('activateDevice.latitude')"
                  min="-90"
                  max="90"
                  maxlength="12"
                  :rules="latitudeRules"
                  @keypress="preventNonCoords($event)"
                  data-cy="deviceSettingsLatitude"
                  :class="{ 'text-field-required': locationTab.allowSetLocation }"
                  :disabled="!locationTab.allowSetLocation"
                ></v-text-field>
                <v-text-field
                  :label="$t('activateDevice.longitude')"
                  persistent-hint
                  v-model="locationTab.coords.longitude"
                  min="-180"
                  max="180"
                  maxlength="13"
                  :rules="longitudeRules"
                  @keypress="preventNonCoords($event)"
                  data-cy="deviceSettingsLongitude"
                  :class="{ 'text-field-required': locationTab.allowSetLocation }"
                  :disabled="!locationTab.allowSetLocation"
                ></v-text-field>
                <div
                  class="hint-text"
                  :class="{ 'hint-text--disabled': !locationTab.allowSetLocation }"
                >
                  {{ $t('activateDevice.coordinateHint') }}
                  <a
                    :href="$t('activateDevice.coordinateHintLink')"
                    class="hint-text-link"
                    target="_blank"
                    >{{ $t('activateDevice.coordinateHintLink') }}</a
                  >
                </div>
              </div>
            </v-card>
          </v-form>
        </v-window-item>

        <v-window-item :eager="true" value="calibrationTab">
          <v-form ref="calibrationTab">
            <DeviceCalibration
              :deviceModel="generalTab.deviceModel"
              :userCals="calibrationTab.userCals"
              :userZeros="calibrationTab.userZeros"
              :permissions="permissions"
              @validated="onValidation"
              @requestZero="requestZero"
              @cancelZero="cancelZero"
            />
          </v-form>
        </v-window-item>
      </v-tabs-window>
    </template>
    <template v-if="showDefaultView" v-slot:actions>
      <v-btn @click="close" variant="outlined" class="text-black" data-cy="deviceSettingsClose">{{
        $t('buttons.cancel')
      }}</v-btn>
      <v-spacer></v-spacer>
      <v-btn
        @click="save"
        class="bg-primary text-white"
        :disabled="disableSave"
        variant="text"
        data-cy="deviceSettingsSave"
        >{{ $t('buttons.save') }}</v-btn
      >
    </template>
    <!-- unregister device view actions -->
    <template v-else-if="unregisterWarningOpen" v-slot:actions>
      <DeviceUnregisterActions
        :deviceId="deviceId"
        @device-unregistering="handleDeviceUnregistering"
        @unregister="handleUnregister"
        @close="unregisterWarningOpen = false"
      />
    </template>
    <!-- zeroing confirmation view actions -->
    <template v-else-if="zeroConfirmOpen" v-slot:actions>
      <DeviceZeroConfirmationActions @confirm="confirmAddZero" @cancel="zeroConfirmOpen = false" />
    </template>
    <!-- unregister device view body -->
    <span v-if="unregisterWarningOpen">
      <DeviceUnregisterCard :deviceId="deviceId" :deviceUnregistering="deviceUnregistering" />
    </span>
    <!-- zeroing confirmation view body -->
    <span v-if="zeroConfirmOpen">
      <DeviceZeroConfirmation :zeroValue="!!calibrationTab.queuedZeroRequest.zeroValue" />
    </span>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useLocationGetter, locationCoordsRegex } from '@/services/location'
import type { VForm } from 'vuetify/components'
import type { LocationCoords } from '@/store/location'
import {
  defaultLoggingInterval,
  defaultCleaningInterval,
  writeDeviceConfig,
  getConfigByKey
} from '@/services/device-config'
import { getUserCals, type UserCalibration } from '@/services/device-calibration'
import { getUserZeros, type UserZero } from '@/services/device-zeroing'
import { DEVICE_UPDATE_INTERVAL_MS } from '@/services/device-metadata'
import { checkStatus } from '@/api/services/utils'
import DeviceUnregisterCard from '@/components/deviceUnregister/DeviceUnregisterCard.vue'
import DeviceUnregisterActions from '@/components/deviceUnregister/DeviceUnregisterActions.vue'
import DeviceCalibration from '@/components/deviceSettings/DeviceCalibration.vue'
import DeviceZeroConfirmation from '@/components/deviceSettings/DeviceZeroConfirmation.vue'
import DeviceZeroConfirmationActions from '@/components/deviceSettings/DeviceZeroConfirmationActions.vue'
import DeviceAttributeTextarea from '@/components/deviceSettings/DeviceAttributeTextarea.vue'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS, DEVICE_ACTIONS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { UpdateDeviceRequest } from '@/types/devices'

const loggingIntervalTypes = {
  minute1: 60,
  minute5: 300,
  minute10: 600,
  minute15: 900,
  minute30: 1800,
  minute60: 3600
}

const cleaningIntervalTypes = {
  day1: 86400,
  day7: 604800
}

type TabKey = 'generalTab' | 'locationTab' | 'calibrationTab'

interface GeneralTabState {
  valid: boolean
  deviceName: string
  deviceModel: string
  deviceModelName: string
  enablePublicToggle: boolean
  isPublic: boolean
  indoorSensor: boolean
  isIndoorOnly: boolean
  showLocation: boolean
  showIndoorSensor: boolean
  showLoggingCleaningIntervals: boolean
  privacyZone: boolean
  calibrationFactors: {
    pm25: number
    pm10: number
  }
}

interface LocationTabState {
  locationName: string
  valid: boolean
  allowSetLocation: boolean
  coords: {
    latitude: string
    longitude: string
  }
}

interface CalibrationTabState {
  show: boolean
  valid: boolean
  userCals: UserCalibration[]
  userZeros: Record<string, UserZero>
  queuedZeroRequest: {
    zeroValue: boolean | null
    sensorName: string | null
  }
}

const getDefaultGeneralTabState = (): GeneralTabState => ({
  valid: false,
  deviceName: '',
  deviceModel: '',
  deviceModelName: '',
  enablePublicToggle: false,
  isPublic: false,
  indoorSensor: false,
  isIndoorOnly: false,
  showLocation: false,
  showIndoorSensor: false,
  showLoggingCleaningIntervals: false,
  privacyZone: false,
  calibrationFactors: {
    pm25: 1,
    pm10: 1
  }
})

const getDefaultLocationTabState = (): LocationTabState => ({
  locationName: '',
  valid: false,
  allowSetLocation: false,
  coords: {
    latitude: '',
    longitude: ''
  }
})

const getDefaultCalibrationTabState = (): CalibrationTabState => ({
  show: false,
  valid: false,
  userCals: [],
  userZeros: {},
  queuedZeroRequest: {
    zeroValue: null,
    sensorName: null
  }
})

export default defineComponent({
  name: 'DeviceSettingsDialog',
  props: ['open', 'deviceId', 'permissions'],
  emits: {
    close: (_payload: { deviceId: string; coords: { lng: number; lat: number } }) => true,
    unregister: () => true,
    afterLeave: () => true
  },
  setup() {
    const locationGetter = useLocationGetter()
    return { ...locationGetter }
  },
  components: {
    DeviceAttributeTextarea,
    DeviceCalibration,
    DeviceUnregisterCard,
    DeviceUnregisterActions,
    DeviceZeroConfirmation,
    DeviceZeroConfirmationActions
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      tab: null as TabKey | null,
      unregisterWarningOpen: false,
      deviceUnregistering: false,
      zeroConfirmOpen: false,
      gettingLocation: false,
      locationError: false,
      locationErrorMessage: '',
      updateError: false,
      requiredError: false,
      calibrationError: false,
      loadStateInterval: null as NodeJS.Timeout | null,

      generalTab: getDefaultGeneralTabState(),
      locationTab: getDefaultLocationTabState(),
      calibrationTab: getDefaultCalibrationTabState(),

      loggingInterval: null as number | null, // this is the default selection
      cleaningInterval: null as number | null, //set from api
      rules: {
        empty: (value: string) => value === null || value.length === 0,
        required: (value: string) => !!value || this.$t('validation.required'),
        latitude: (value: string) => {
          return locationCoordsRegex.latitude.test(value) || this.$t('validation.invalidEntry')
        },
        longitude: (value: string) => {
          return locationCoordsRegex.longitude.test(value) || this.$t('validation.invalidEntry')
        }
      }
    }
  },
  mounted() {
    this.completeDialogData()
  },
  watch: {
    open() {
      this.completeDialogData()
    }
  },
  computed: {
    latitudeRules() {
      if (this.generalTab.showLocation) {
        return [this.rules.required, this.rules.latitude]
      }
      return []
    },
    longitudeRules() {
      if (this.generalTab.showLocation) {
        return [this.rules.required, this.rules.longitude]
      }
      return []
    },
    showDefaultView() {
      return !this.unregisterWarningOpen && !this.zeroConfirmOpen
    },
    cleaningIntervalItems() {
      return [
        {
          title: this.$t('map.deviceSettings.cleaningIntervals.day1'),
          value: cleaningIntervalTypes.day1
        },
        {
          title: this.$t('map.deviceSettings.cleaningIntervals.day7'),
          value: cleaningIntervalTypes.day7
        }
      ]
    },
    loggingIntervalItems() {
      return [
        {
          title: this.$t('map.deviceSettings.loggingIntervals.minute1'),
          value: loggingIntervalTypes.minute1
        },
        {
          title: this.$t('map.deviceSettings.loggingIntervals.minute5'),
          value: loggingIntervalTypes.minute5
        },
        {
          title: this.$t('map.deviceSettings.loggingIntervals.minute10'),
          value: loggingIntervalTypes.minute10
        },
        {
          title: this.$t('map.deviceSettings.loggingIntervals.minute15'),
          value: loggingIntervalTypes.minute15
        },
        {
          title: this.$t('map.deviceSettings.loggingIntervals.minute30'),
          value: loggingIntervalTypes.minute30
        },
        {
          title: this.$t('map.deviceSettings.loggingIntervals.minute60'),
          value: loggingIntervalTypes.minute60
        }
      ]
    },
    fieldsValid() {
      const lng = parseFloat(this.locationTab.coords.longitude)
      const lat = parseFloat(this.locationTab.coords.latitude)

      const hasName = this.rules.required(this.generalTab.deviceName) === true
      const latRulesSatisfied = this.latitudeRules.every((rule) => rule(String(lat)) === true)
      const lngRulesSatisfied = this.longitudeRules.every((rule) => rule(String(lng)) === true)

      return latRulesSatisfied && lngRulesSatisfied && hasName && !this.calibrationError
    },
    isCalibrationTab() {
      return this.tab === 'calibrationTab'
    },
    hasInvalidTabs() {
      if (!this.generalTab.valid || !this.locationTab.valid || !this.calibrationTab.valid) {
        return true
      }
      return false
    },
    error() {
      if (this.isLoading) {
        return ''
      }
      if (this.updateError) {
        return this.$t('map.deviceSettings.failedToUpdate')
      }
      if (this.requiredError) {
        return this.$t('activateDevice.validationRequiredError')
      }
      if (this.unregisterWarningOpen || this.zeroConfirmOpen) {
        // There are no required fields during unregistration or zeroing confirmation
        // suppress the error message
        return ''
      }
      if (this.isCalibrationTab) {
        return ''
      }
      return this.$t('validation.requiredHelp')
    },
    secondaryError() {
      if (this.isLoading) {
        return ''
      }
      if (this.unregisterWarningOpen || this.zeroConfirmOpen) {
        // There are no required fields during unregistration or zeroing confirmation
        // suppress the error message
        return ''
      }
      if (this.hasInvalidTabs) {
        return this.$t('map.deviceSettings.completeAllTabs')
      }
      return ''
    },
    isShaded() {
      if (this.unregisterWarningOpen || this.zeroConfirmOpen) {
        return false
      }
      return true
    },
    deviceModelKey(): string {
      const model = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ](this.deviceId)
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
      ](model)
    },
    deviceImgPackageName() {
      const dustTrakImgPackage = 'dusttrak'
      const modelKey = this.deviceModelKey
      if (modelKey?.includes(dustTrakImgPackage)) {
        return dustTrakImgPackage
      }

      return modelKey
    },
    indoorSensorHint() {
      if (this.generalTab.isIndoorOnly) {
        return this.$t('activateDevice.isIndoorOnlyHint', {
          modelName: this.generalTab.deviceModelName
        })
      }
      return ''
    },
    indoorPinSrc(): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_PIN_PATH}`
      ](this.generalTab.deviceModel, 'indoor')
    },
    outdoorPinSrc(): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_PIN_PATH}`
      ](this.generalTab.deviceModel, 'outdoor')
    },
    tabSliderColor() {
      return !this[this.tab]?.valid ? 'error' : 'currentColor'
    },
    disableSave() {
      return !this.fieldsValid || this.isLoading || this.isSaving
    }
  },
  methods: {
    async init() {
      this.gettingLocation = false
      this.locationError = false
      this.locationErrorMessage = ''
      this.updateError = false
      this.requiredError = false
      this.calibrationError = false

      this.generalTab = getDefaultGeneralTabState()
      this.locationTab = getDefaultLocationTabState()
      this.calibrationTab = getDefaultCalibrationTabState()

      await this.loadState()
      await this.getDeviceConfig()
      this.startLoadStateInterval()

      this.validateTabs()
    },
    async completeDialogData() {
      if (this.open) {
        this.init()
        this.completeDeviceData()
        this.completeDeviceMetadata()
      }
    },
    completeDeviceData() {
      if (this.deviceId) {
        this.generalTab.deviceName = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`
        ](this.deviceId)
        this.generalTab.deviceModel = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
        ](this.deviceId)
        this.generalTab.isPublic = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_PUBLIC_BY_DEVICE_ID}`
        ](this.deviceId)
        this.generalTab.indoorSensor = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_INDOOR_BY_DEVICE_ID}`
        ](this.deviceId)
        this.locationTab.locationName = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LOCATION_NAME_BY_DEVICE_ID}`
        ](this.deviceId)

        const coords = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`
        ](this.deviceId)
        if (coords) {
          this.locationTab.coords.latitude = coords.lat
          this.locationTab.coords.longitude = coords.lng
        }

        if (this.generalTab.deviceModel) {
          this.generalTab.deviceModelName = this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_NAME}`
          ](this.generalTab.deviceModel)
        }

        const indoorOnly = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_INDOOR_ONLY}`
        ](this.generalTab.deviceModel)
        if (indoorOnly) {
          this.generalTab.indoorSensor = true
          this.generalTab.isIndoorOnly = true
        }
      }
    },
    completeDeviceMetadata() {
      if (this.deviceId) {
        const showLocation = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_LOCATION}`
        ](this.generalTab.deviceModel)
        if (showLocation) {
          this.generalTab.showLocation = true
          this.locationTab.allowSetLocation = true
        }
        const showCalibration = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_CALIBRATION}`
        ](this.generalTab.deviceModel)
        if (showCalibration) {
          this.calibrationTab.show = true
        }
        const showIndoorSensor = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_INDOOR_SENSOR}`
        ](this.generalTab.deviceModel)
        if (showIndoorSensor) {
          this.generalTab.showIndoorSensor = true
        }
        const showLoggingCleaningIntervalSettings = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_INTERVAL_SETTINGS}`
        ](this.generalTab.deviceModel)
        if (showLoggingCleaningIntervalSettings) {
          this.generalTab.showLoggingCleaningIntervals = true
        }
        const enablePublicToggle = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_ALLOW_PUBLIC}`
        ](this.generalTab.deviceModel)
        if (enablePublicToggle) {
          this.generalTab.enablePublicToggle = true
        }
      }
    },
    close() {
      this.stopLoadStateInterval()
      this.$emit('close', {
        deviceId: this.deviceId,
        coords: {
          lng: parseFloat(this.locationTab.coords.longitude),
          lat: parseFloat(this.locationTab.coords.latitude)
        }
      })

      this.closeUnregisterWarning()
    },
    afterLeave() {
      this.tab = null
      this.$emit('afterLeave')
    },
    startLoadStateInterval() {
      if (this.loadStateInterval === null) {
        this.loadStateInterval = setInterval(this.loadState, DEVICE_UPDATE_INTERVAL_MS)
      }
    },
    stopLoadStateInterval() {
      if (this.loadStateInterval !== null) {
        clearInterval(this.loadStateInterval)
        this.loadStateInterval = null
      }
    },
    closeUnregisterWarning() {
      // delay to give time for the closing animation
      setTimeout(() => {
        this.unregisterWarningOpen = false
      }, 100)
    },
    handleDeviceUnregistering(isUnregistering: boolean) {
      this.deviceUnregistering = isUnregistering
    },
    handleUnregister: function () {
      this.$emit('unregister')
      this.close()
    },
    setCoords(coords: LocationCoords | false) {
      if (coords) {
        if (coords.lat && coords.lng) {
          this.locationTab.coords.latitude = `${coords.lat.toFixed(8)}`
          this.locationTab.coords.longitude = `${coords.lng.toFixed(8)}`
        } else {
          this.locationError = true
          this.locationErrorMessage = this.$t('activateDevice.locationNotAvailable')
        }
      } else {
        this.locationError = true
        this.locationErrorMessage = this.$t('activateDevice.locationDenied')
      }
      this.gettingLocation = false
    },
    useBrowserLocation() {
      this.gettingLocation = true

      this.getGeolocation((coords: LocationCoords | false) => {
        this.setCoords(coords)
      })
    },
    preventNonCoords(event: KeyboardEvent) {
      // NOTE: may need to modify this to prevent input like "--4.323" or "0100..43" etc
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '-', '.']
      if (allowedKeys.includes(event.key)) return
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    async loadState() {
      this.$store.dispatch(
        `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_DEVICE_DETAILS_AND_UPDATE}`,
        { deviceId: this.deviceId }
      )
    },
    async save() {
      this.isSaving = true
      this.requiredError = false
      this.updateError = false

      const deviceData: UpdateDeviceRequest = {
        cloud_state_data: {
          name: this.generalTab.deviceName,
          locationName: this.locationTab.locationName,
          isPublic: this.generalTab.isPublic,
          isIndoor: this.generalTab.indoorSensor,
          longitude: parseFloat(this.locationTab.coords.longitude),
          latitude: parseFloat(this.locationTab.coords.latitude)
        }
      }

      const config = writeDeviceConfig(
        this.loggingInterval,
        this.cleaningInterval,
        this.calibrationTab.userCals,
        this.calibrationTab.userZeros
      )

      if (!this.hasAllRequiredData(deviceData)) {
        this.requiredError = true
        return
      }

      try {
        const updateResp = await this.$api.patchUpdateDevice(deviceData, this.deviceId)
        if (!updateResp.ok) {
          this.updateError = true
          return
        }
        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_DEVICE_COORDS}`, {
          deviceId: this.deviceId,
          coords: {
            lng: deviceData.cloud_state_data.longitude,
            lat: deviceData.cloud_state_data.latitude
          }
        })

        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_METADATA}`, {
          deviceId: this.deviceId,
          isPublic: this.generalTab.isPublic,
          indoor: this.generalTab.indoorSensor,
          name: this.generalTab.deviceName,
          locationName: this.locationTab.locationName
        })

        /* Save config for devices that show logging intervals, cleaning intervals, or calibration tab.
        Since DustTrak devices are auto-registered and the config is not created during this process,
        don't try to update config data for SB connected devices.
        It returns an error and prevents the device settings from be saved. */
        if (this.generalTab.showLoggingCleaningIntervals || this.calibrationTab.show) {
          const configResp = await this.$api.postDeviceConfig(config, this.deviceId)
          if (!configResp.ok) {
            this.updateError = true
            return
          }
        }

        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_USER_CALS}`, {
          deviceId: this.deviceId,
          userCals: this.calibrationTab.userCals
        })

        this.close()
      } catch {
        this.updateError = true
      }

      this.isSaving = false
    },
    hasAllRequiredData(deviceData: UpdateDeviceRequest) {
      return (
        this.rules.required(deviceData.cloud_state_data.name) === true &&
        this.latitudeRules.every((rule) => rule(String(deviceData.cloud_state_data.latitude))) &&
        this.longitudeRules.every((rule) => rule(String(deviceData.cloud_state_data.longitude)))
      )
    },
    onValidation(v: { valid: boolean; type: string }) {
      if (v.type === 'calibration') {
        this.calibrationTab.valid = v.valid
        this.calibrationError = !v.valid
      }
    },
    storeDevicePin(dev: Record<string, unknown>) {
      const coords = { lat: dev.latitude, lng: dev.longitude }
      this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_DEVICE_COORDS}`, {
        deviceId: this.deviceId,
        coords: coords
      })

      this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_METADATA}`, {
        deviceId: this.deviceId,
        name: dev.friendlyName,
        indoor: dev.indoorSensor
      })

      this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_IS_PUBLIC}`, {
        deviceId: this.deviceId,
        isPublic: dev.isPublic
      })
    },
    async getDeviceConfig() {
      this.isLoading = true

      const resp = await this.$api.getDeviceConfig(this.deviceId)
      if (checkStatus(resp)) {
        const body = await resp.json()
        const sensorConfig = body.sensor_config
        const cloudConfig = body.cloud_config
        this.loggingInterval =
          (getConfigByKey(sensorConfig, 'send_rate') as number) || defaultLoggingInterval
        this.cleaningInterval =
          (getConfigByKey(sensorConfig, 'pm_clean_interval') as number) || defaultCleaningInterval

        const model = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
        ](this.deviceId)
        const subModel = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
        ](this.deviceId)
        const modelCalibrationMeta = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_CALIBRATION_LIMITS}`
        ](model, subModel)

        this.calibrationTab.userCals = getUserCals(sensorConfig, modelCalibrationMeta)

        const zeroableSensors = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_ZEROABLE_MEASUREMENTS}`
        ](model, subModel)
        const zeroAppliedAtMap = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ZERO_APPLIED_AT_BY_DEVICE_ID}`
        ](this.deviceId)
        const zeroStateMap = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ZERO_STATE_BY_DEVICE_ID}`
        ](this.deviceId)

        this.calibrationTab.userZeros = getUserZeros(
          zeroableSensors,
          cloudConfig,
          zeroAppliedAtMap,
          zeroStateMap
        )
      }

      this.isLoading = false
    },
    requestZero(sensorName: string, zeroValue: boolean) {
      this.calibrationTab.queuedZeroRequest.sensorName = sensorName
      this.calibrationTab.queuedZeroRequest.zeroValue = zeroValue
      this.zeroConfirmOpen = true
    },
    cancelZero(sensorName: string) {
      this.calibrationTab.userZeros[sensorName].userzero = null
    },
    confirmAddZero() {
      if (
        this.calibrationTab.queuedZeroRequest.sensorName !== null &&
        this.calibrationTab.queuedZeroRequest.zeroValue !== null
      ) {
        this.calibrationTab.userZeros[this.calibrationTab.queuedZeroRequest.sensorName].userzero =
          this.calibrationTab.queuedZeroRequest.zeroValue

        this.calibrationTab.queuedZeroRequest.sensorName = null
        this.calibrationTab.queuedZeroRequest.zeroValue = null
      }

      this.zeroConfirmOpen = false
    },
    validateTabs() {
      ;(this.$refs.generalTab as VForm)?.validate()
      ;(this.$refs.locationTab as VForm)?.validate()
    }
  },
  beforeUnmount() {
    this.close()
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/global-variables' as *;

.v-dialog {
  .v-card {
    padding: 16px;
    .v-input {
      position: relative;
      z-index: 1;
      padding-top: 24px;
    }
    .secondary-indicator {
      display: flex;
      .device-type-indicator {
        align-self: center;
        margin-left: 16px;
        img {
          height: 36px;
        }
      }
    }

    .v-progress-circular {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 100;
      transform: translate3d(-50%, -50%, 0);
    }
  }
  .v-card-title.tab-content__title {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 0 0 0 0;
    opacity: 0.56;
  }

  .hint-text {
    font-size: 12px;

    &--disabled {
      opacity: var(--v-disabled-opacity);
      pointer-events: none;
    }
  }
}

.v-list-item--disabled {
  pointer-events: auto;
  cursor: not-allowed;
  padding-left: 0;
  padding-right: 0;
  .v-list-item__content {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.v-tabs {
  transition: all 0.2s ease;
  &[inert] {
    opacity: 0.5;
    pointer-events: none;
  }
  .v-tab {
    flex: 1 1 100%;
  }
  .v-tab.text-error {
    position: relative;
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background-color: currentColor;
      opacity: 0.4;
      .dialog-card--shaded & {
        display: none;
      }
    }
    .tab-title {
      position: relative;
      &:after {
        content: '*';
        position: absolute;
        left: 100%;
        margin-left: 4px;
        vertical-align: super;
        font-size: smaller;
        line-height: 0.75;
      }
    }
  }
}

.v-tabs-window {
  &[inert] {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
